import { useQuery } from '@apollo/client'
import { Text } from '@fable/components'
import { css, cx } from '@fable/theme'
import { useNavigate } from 'react-router-dom'
import Loader from '../../../../components/loader'
import Slider, { SliderSettings } from '../../../../components/slider'
import { BREAKPOINTS } from '../../../../constants'
import { GET_GENRES_LINKS } from '../../../../queries'
import { Genre } from '../../../../types'
import { Color } from '@fable/legacy'
import { routes } from '../../routes'

interface Props {
  currentGenre: string
}

export const GenresSlider: React.FC<Props> = ({ currentGenre }) => {
  const navigate = useNavigate()
  const { data, loading, error } = useQuery(GET_GENRES_LINKS)

  if (error) return null

  if (loading)
    return (
      <Loader
        className={css`
          height: 250px;
        `}
      />
    )

  const genres = data.genreCollection.items

  const renderGenres = genres
    .filter((genre: Genre) => genre.name !== currentGenre && genre.logo)
    .map((genre: Genre, index: number) => {
      const { color, logo, name, slug } = genre
      const path = `${routes.genres}/${slug}`

      return (
        <div
          key={index}
          onClick={() => navigate(path)}
          style={{ backgroundColor: color }}
          className={cx(styles.genre)}
        >
          <img
            className={cx(styles.genreLogo)}
            src={logo.url}
            alt={`${name} Icon`}
          />
        </div>
      )
    })

  const sliderConfig: SliderSettings = {
    infinite: true,
    speed: 500,
    slidesToScroll: 1,
    slidesToShow: 3,
    responsive: [
      {
        breakpoint: 1326,
        settings: {
          slidesToShow: 2,
        },
      },
      {
        breakpoint: 960,
        settings: {
          slidesToShow: 3,
        },
      },
      {
        breakpoint: 648,
        settings: {
          slidesToShow: 2,
        },
      },
    ],
  }

  return (
    <div className={cx(styles.wrapper)}>
      <Text className={styles.label} type="label" sizing="L">
        Other categories
      </Text>
      <Slider
        wrapperClass={cx(styles.slider)}
        config={sliderConfig}
        hasControls={true}
        itemCount={genres.length}
        compact
      >
        {renderGenres}
      </Slider>
    </div>
  )
}

const styles = {
  wrapper: css`
    margin: 54px 0 0;

    @media only screen and (max-width: 960px) {
      margin-bottom: 60px;
    }
  `,
  slider: css`
    margin-top: 14px;

    .slick-list {
      margin-left: 28px;
      width: calc(100% - 56px);
    }

    .slick-slide {
      padding: 24px 4px 0;
    }

    @media only screen and (max-width: ${BREAKPOINTS.mobile}) {
      .slick-list {
        padding: 0;
        width: calc(100% + 4px);
        margin-left: -2px;
      }

      .slick-slide {
        padding: 24px 2px 0;
      }
    }
  `,
  label: css`
    padding-left: 32px;
    font-size: 24px !important;
    line-height: 24px !important;
    letter-spacing: 0;

    @media only screen and (max-width: ${BREAKPOINTS.mobile}) {
      padding-left: 24px;
      font-size: 16px !important;
      line-height: 24px !important;
    }
  `,
  genre: css`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 160px;
    border-radius: 50px 5px 50px 5px;
    position: relative;
    overflow: hidden;
    cursor: pointer;

    @media only screen and (max-width: ${BREAKPOINTS.mobile}) {
      height: 32vw;
    }

    :before {
      content: '';
      width: 100%;
      height: 100%;
      background-color: ${Color.whiteFang};
      position: absolute;
      top: 0;
      left: 0;
      opacity: 0;
      transition: opacity ease 0.2s;
    }

    :hover::before {
      opacity: 0.15;
    }
  `,
  genreLogo: css`
    display: block;
    max-width: 60%;
    max-height: 40%;
    object-fit: contain;

    @media only screen and (max-width: ${BREAKPOINTS.mobile}) {
      max-width: 60%;
      height: 40%;
    }
  `,
}
